import React, { useEffect, useState } from 'react';
import {
  DollarSign, Wallet, TrendingUp, Clock, ShoppingCart, FileClock,
  FileCheck, AlertCircle, BadgeDollarSign,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import api from '@/lib/api';
import { formatBRL, statusInfo } from '@/lib/format';
import { useAuth } from '@/context/AuthContext';

function KpiCard({ icon: Icon, label, value, sub, accent, testid }) {
  return (
    <Card className="p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{label}</span>
        <span className={`h-9 w-9 rounded-lg flex items-center justify-center ${accent || 'bg-primary/10 text-primary'}`}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <div className="mt-3 font-display text-2xl font-semibold tabular-nums">{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </Card>
  );
}

export default function VendorDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/finance/my-dashboard')
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-muted-foreground">Carregando seu painel...</p>;
  if (!data) return <p className="text-muted-foreground">Não foi possível carregar o painel.</p>;
  const k = data.kpis;

  return (
    <div className="space-y-6" data-testid="vendor-dashboard">
      <div>
        <h2 className="font-display text-2xl font-semibold">Olá, {user?.name?.split(' ')[0] || 'Vendedor'}</h2>
        <p className="text-sm text-muted-foreground">Acompanhe suas vendas, comissões e pendências.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={DollarSign} label="Total vendido" value={formatBRL(k.total_vendido)} sub={`${k.total_vendas} venda(s)`} testid="vendor-kpi-vendido" />
        <KpiCard icon={BadgeDollarSign} label="Comissão total" value={formatBRL(k.comissao_total)} accent="bg-[hsl(174_55%_34%/0.15)] text-[hsl(174_55%_28%)]" testid="vendor-kpi-comissao" />
        <KpiCard icon={Clock} label="Comissão pendente" value={formatBRL(k.comissao_pendente)} sub={`${formatBRL(k.comissao_recebida)} já liberada`} accent="bg-[hsl(36_55%_55%/0.18)] text-[hsl(36_60%_35%)]" testid="vendor-kpi-comissao-pendente" />
        <KpiCard icon={Wallet} label="A receber" value={formatBRL(k.a_receber)} sub={`${formatBRL(k.total_recebido)} recebido`} accent="bg-destructive/10 text-destructive" testid="vendor-kpi-areceber" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={ShoppingCart} label="Minhas vendas" value={k.total_vendas} testid="vendor-kpi-vendas" />
        <KpiCard icon={TrendingUp} label="Comissão liberada" value={formatBRL(k.comissao_recebida)} accent="bg-[hsl(174_55%_34%/0.15)] text-[hsl(174_55%_28%)]" testid="vendor-kpi-comissao-recebida" />
        <KpiCard icon={FileClock} label="Contratos pendentes" value={k.contratos_pendentes} accent="bg-[hsl(36_55%_55%/0.18)] text-[hsl(36_60%_35%)]" testid="vendor-kpi-contratos" />
        <KpiCard icon={DollarSign} label="Total recebido" value={formatBRL(k.total_recebido)} testid="vendor-kpi-recebido" />
      </div>

      {/* Pendências */}
      <Card className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <AlertCircle className="h-4 w-4 text-[hsl(36_60%_45%)]" />
          <h3 className="font-semibold">Pendências</h3>
          <Badge variant="secondary" className="ml-1">{data.pendentes.length}</Badge>
        </div>
        {data.pendentes.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Nenhuma pendência. Tudo em dia!</div>
        ) : (
          <div className="space-y-2" data-testid="vendor-pendencias">
            {data.pendentes.map((v) => (
              <div key={v.sale_id} className="flex items-center justify-between gap-3 py-2 border-b border-border/50 last:border-0">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{v.cliente_nome} — {v.trip_nome}</div>
                  <div className="text-xs text-muted-foreground flex flex-wrap gap-2 mt-0.5">
                    {v.saldo > 0 && <span>Saldo a receber: {formatBRL(v.saldo)}</span>}
                    {!v.contrato_assinado && <span className="inline-flex items-center gap-1"><FileClock className="h-3 w-3" /> Contrato não assinado</span>}
                  </div>
                </div>
                {v.contrato_assinado
                  ? <Badge className="bg-[hsl(174_55%_34%/0.15)] text-[hsl(174_55%_24%)] hover:bg-[hsl(174_55%_34%/0.15)] gap-1 shrink-0"><FileCheck className="h-3 w-3" /> Assinado</Badge>
                  : <Badge variant="secondary" className="gap-1 shrink-0"><FileClock className="h-3 w-3" /> Pendente</Badge>}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Minhas vendas */}
      <Card className="p-4">
        <h3 className="font-semibold mb-3 px-1">Minhas vendas</h3>
        {data.vendas.length === 0 ? (
          <div className="py-12 text-center">
            <ShoppingCart className="h-8 w-8 mx-auto text-muted-foreground" />
            <p className="mt-3 text-muted-foreground">Você ainda não registrou vendas.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="vendor-sales-table">
              <TableHeader><TableRow>
                <TableHead>Cliente</TableHead><TableHead>Viagem</TableHead>
                <TableHead className="text-right">Valor</TableHead><TableHead className="text-right">Pago</TableHead>
                <TableHead className="text-right">Saldo</TableHead><TableHead className="text-right">Comissão</TableHead>
                <TableHead>Status</TableHead><TableHead>Contrato</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.vendas.map((v) => {
                  const si = statusInfo(v.status);
                  return (
                    <TableRow key={v.sale_id}>
                      <TableCell className="font-medium">{v.cliente_nome}</TableCell>
                      <TableCell className="text-muted-foreground">{v.trip_nome}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatBRL(v.valor_total)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatBRL(v.valor_pago)}</TableCell>
                      <TableCell className={`text-right tabular-nums ${v.saldo > 0 ? 'text-destructive' : ''}`}>{formatBRL(v.saldo)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatBRL(v.comissao_valor)}</TableCell>
                      <TableCell><span className={`text-xs px-2 py-0.5 rounded-full ${si.cls}`}>{si.label}</span></TableCell>
                      <TableCell>{v.contrato_assinado
                        ? <Badge className="bg-[hsl(174_55%_34%/0.15)] text-[hsl(174_55%_24%)] hover:bg-[hsl(174_55%_34%/0.15)] gap-1"><FileCheck className="h-3 w-3" /> Assinado</Badge>
                        : <Badge variant="secondary" className="gap-1"><FileClock className="h-3 w-3" /> Pendente</Badge>}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
